<script setup>
  import {computed, ref, watch} from "vue";

  import ComponentDropdown from "@components/Dropdown/Dropdown.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {MedCheckbox, MedFormField, MedLabel} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "ComponentFilterFlags",
  });

  const props = defineProps({
    modelValue: {
      type: Object,
      required: true,
    },
    availableFlags: {
      type: Object,
      required: true,
    },
    onlyOneAllowed: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["update:modelValue"]);

  const flags = ref(props.modelValue);

  watch(flags, (newValue) => {
    emit("update:modelValue", newValue);
  });

  const flagsCount = computed(() => {
    return flags.value.filter((flag) => {
      return !flag.includes(":");
    }).length;
  });

  function allowsSubSelect(flag) {
    return typeof flag === "object";
  }

  function getCurrentSubSelectedLabel(key) {
    let oKey = flags.value.find((flag) => flag.startsWith(key + ":")) ?? null;

    if (oKey) {
      return props.availableFlags[key].options[oKey.split(":")[1]];
    }

    return null;
  }

  function removeFlag(key) {
    flags.value = flags.value.filter((flag) => flag !== key && !flag.startsWith(key + ":"));
  }

  function handleCheckboxChange(key, checked) {
    if (checked) {
      if (!flags.value.includes(key)) {
        flags.value.push(key);
      }

      if (allowsSubSelect(props.availableFlags[key]) && props.availableFlags[key]?.nullable === false) {
        flags.value.push(key + ":" + Object.keys(props.availableFlags[key].options)[0]);
      }

      if (props.onlyOneAllowed && flags.value.length > 1) {
        flags.value = [key];
      }
    } else {
      flags.value = flags.value.filter((f) => f !== key && !f.startsWith(key + ":"));
    }
  }

  function handleSelectedOption(key, event) {
    // remove previous options (unique selection)
    flags.value = flags.value.filter((flag) => !flag.startsWith(key + ":"));

    // now add the selected item (if not empty)
    if (event.target.value !== "") {
      flags.value.push(event.target.value);
    }
  }
</script>

<template>
  <div class="flex flex-row-reverse items-baseline space-x-2 space-x-reverse">
    <ComponentDropdown :test-id="getTestId('filter-flags')">
      <template #button>
        <div
          class="flex w-full items-center justify-between px-2 py-1.5 font-semibold text-gray-500 hover:text-gray-900"
        >
          <span>Filter</span>

          <span
            v-if="flagsCount"
            class="ml-1.5 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-700 tabular-nums"
            v-text="flagsCount"
          />

          <ComponentIcon class="ml-1">expand_more</ComponentIcon>
        </div>
      </template>

      <template #dropdown>
        <ul
          class="max-h-96 min-w-60 flex-col space-y-4 overflow-y-auto p-2"
          :data-test="getTestId(`filter-flags-dropdown-option`)"
        >
          <li
            v-for="(flag, key, index) in availableFlags"
            :key="key"
            class="flex items-center"
            :data-test="getTestId(`filter-flags-dropdown-option-item-${key}`)"
          >
            <div class="w-full">
              <MedFormField orientation="horizontal" size="sm">
                <MedCheckbox
                  :model-value="flags.includes(key)"
                  variant="primary"
                  :data-test="getTestId(`filter-flags-dropdown-option-item-${index}-checkbox`)"
                  @update:model-value="handleCheckboxChange(key, $event)"
                />

                <MedLabel>
                  <template v-if="allowsSubSelect(flag)">
                    {{ flag.label }}
                  </template>

                  <template v-else>
                    {{ flag }}
                  </template>
                </MedLabel>
              </MedFormField>

              <div v-if="allowsSubSelect(flag) && flags.includes(key)" class="ml-6">
                <!-- TODO REPLACE -->
                <select
                  :data-test="getTestId(`filter-flags-dropdown-option-item-select`)"
                  :aria-label="flag.label"
                  @change="handleSelectedOption(key, $event)"
                >
                  <option
                    v-if="flag?.nullable !== false"
                    value=""
                    :data-test="getTestId(`filter-flags-dropdown-option-item-select-option-placeholder`)"
                  >
                    bitte wählen
                  </option>

                  <option
                    v-for="(oLabel, oKey) in flag.options"
                    :key="oKey"
                    :value="key + ':' + oKey"
                    :selected="flags.includes(key + ':' + oKey)"
                    :data-test="getTestId(`filter-flags-dropdown-option-item-select-option-${index}`)"
                  >
                    {{ oLabel }}
                  </option>
                </select>
              </div>
            </div>
          </li>
        </ul>
      </template>
    </ComponentDropdown>

    <ul class="flex flex-row-reverse flex-wrap">
      <template v-for="key in flags" :key="key">
        <li
          v-if="!key.includes(':')"
          class="mb-2 ml-2 flex items-center rounded-xl bg-gray-200 px-2 py-1 text-sm font-medium whitespace-nowrap text-gray-600"
        >
          <span>
            <template v-if="allowsSubSelect(availableFlags[key])">
              {{ availableFlags[key].label }}

              <span v-if="getCurrentSubSelectedLabel(key) !== null" class="font-normal">
                {{ getCurrentSubSelectedLabel(key) }}
              </span>
            </template>

            <template v-else>
              {{ availableFlags[key] }}
            </template>
          </span>

          <button
            type="button"
            class="group relative -mr-1 size-3.5 rounded-sm hover:bg-gray-500/20"
            :data-test="getTestId(`filter-flags-tag-item-remove-${key}`)"
            @click="removeFlag(key)"
          >
            <span class="sr-only">Filter entfernen</span>

            <svg viewBox="0 0 14 14" class="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75">
              <path d="M4 4l6 6m0-6l-6 6" />
            </svg>

            <span class="absolute -inset-1" />
          </button>
        </li>
      </template>
    </ul>
  </div>
</template>
